import React from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    CircularProgress,
    TextField,
    Autocomplete,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Alert,
    alpha,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    CloudDownload as CloudDownloadIcon,
    School as SchoolIcon,
    GroupAdd as GroupAddIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import useTheme from '@mui/system/useTheme';
import type { PgCollege } from 'api/BaseballRecruits';

export interface ImportRostersSectionProps {
    pgColleges: PgCollege[];
    pgCollegesLoading: boolean;
    pgCollege: PgCollege | null;
    onPgCollegeChange: (val: PgCollege | null) => void;
    pgYear: number;
    onPgYearChange: (year: number) => void;
    importingPg: boolean;
    onImportPgCommitments: () => void;
    pgUnmatched: { name: string; position: string; high_school: string; state: string }[];
    onClearPgUnmatched: () => void;
    collegeLogoUrl: string;
    onClearCollegeLogo: () => void;
    settingLogo: boolean;
    onSetGroupLogo: () => void;
    collegeRosterUrl: string;
    onCollegeRosterUrlChange: (url: string) => void;
    importingCollegeRoster: boolean;
    onImportCollegeRoster: () => void;
    rosterUrl: string;
    onRosterUrlChange: (url: string) => void;
    importingRoster: boolean;
    onImportRoster: () => void;
    isValidMaxprepsUrl: (url: string) => boolean;
    onPgAccordionChange?: (event: React.SyntheticEvent, expanded: boolean) => void;
}

const ImportRostersSection: React.FC<ImportRostersSectionProps> = ({
    pgColleges,
    pgCollegesLoading,
    pgCollege,
    onPgCollegeChange,
    pgYear,
    onPgYearChange,
    importingPg,
    onImportPgCommitments,
    pgUnmatched,
    onClearPgUnmatched,
    collegeLogoUrl,
    onClearCollegeLogo,
    settingLogo,
    onSetGroupLogo,
    collegeRosterUrl,
    onCollegeRosterUrlChange,
    importingCollegeRoster,
    onImportCollegeRoster,
    rosterUrl,
    onRosterUrlChange,
    importingRoster,
    onImportRoster,
    isValidMaxprepsUrl,
    onPgAccordionChange,
}) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: 2,
                p: 1.5,
                background: alpha(theme.palette.primary.main, 0.02),
                order: { xs: 0, md: 0 },
            }}
        >
            <Typography
                variant="overline"
                sx={{
                    color: 'primary.main',
                    fontWeight: 700,
                    textAlign: 'center',
                    mb: 1,
                    letterSpacing: 1,
                    fontSize: '0.85rem',
                }}
            >
                Import Rosters
            </Typography>
            <Accordion
                disableGutters
                elevation={0}
                onChange={onPgAccordionChange}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                    mb: 1,
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CloudDownloadIcon sx={{ color: 'primary.main' }} fontSize="small" />
                        <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                            College Commitment Roster from PG
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Autocomplete
                            size="small"
                            options={pgColleges}
                            getOptionLabel={(o) => o.name}
                            value={pgCollege}
                            onChange={(_, v) => onPgCollegeChange(v)}
                            loading={pgCollegesLoading}
                            disabled={importingPg}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="College"
                                    placeholder="Search colleges..."
                                />
                            )}
                            sx={{ flex: 1 }}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        />
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                            <InputLabel>Year</InputLabel>
                            <Select
                                value={pgYear}
                                label="Year"
                                onChange={(e) => onPgYearChange(Number(e.target.value))}
                                disabled={importingPg}
                            >
                                {Array.from({ length: 8 }, (_, i) => 2024 + i).map((y) => (
                                    <MenuItem key={y} value={y}>
                                        {y}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onImportPgCommitments}
                            disabled={importingPg || !pgCollege}
                            startIcon={
                                importingPg ? <CircularProgress size={16} /> : <CloudDownloadIcon />
                            }
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Import
                        </Button>
                    </Box>
                    {pgUnmatched.length > 0 && (
                        <Alert severity="info" sx={{ mt: 2 }} onClose={onClearPgUnmatched}>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                {pgUnmatched.length} player(s) not found on MaxPreps:
                            </Typography>
                            {pgUnmatched.map((p, i) => (
                                <Typography key={i} variant="caption" sx={{ display: 'block' }}>
                                    {p.name}
                                    {p.position ? ` (${p.position})` : ''}
                                    {p.high_school ? ` — ${p.high_school}` : ''}
                                    {p.state ? `, ${p.state}` : ''}
                                </Typography>
                            ))}
                        </Alert>
                    )}
                    {collegeLogoUrl && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 1.5,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <Box
                                component="img"
                                src={collegeLogoUrl}
                                sx={{
                                    width: 40,
                                    height: 40,
                                    objectFit: 'contain',
                                    borderRadius: 1,
                                    flexShrink: 0,
                                }}
                                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                }}
                            />
                            <Typography variant="body2" sx={{ flex: 1 }}>
                                Use college logo as group photo?
                            </Typography>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={onSetGroupLogo}
                                disabled={settingLogo}
                                startIcon={settingLogo ? <CircularProgress size={14} /> : undefined}
                            >
                                {settingLogo ? 'Setting...' : 'Set Logo'}
                            </Button>
                            <IconButton size="small" onClick={onClearCollegeLogo}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                </AccordionDetails>
            </Accordion>
            <Accordion
                disableGutters
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                    mb: 1,
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SchoolIcon sx={{ color: 'primary.main' }} fontSize="small" />
                        <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                            Current College Roster
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="https://thesundevils.com/sports/baseball/roster"
                            value={collegeRosterUrl}
                            onChange={(e) => onCollegeRosterUrlChange(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') onImportCollegeRoster();
                            }}
                            disabled={importingCollegeRoster}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onImportCollegeRoster}
                            disabled={importingCollegeRoster || !collegeRosterUrl.trim()}
                            startIcon={
                                importingCollegeRoster ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <SchoolIcon />
                                )
                            }
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Import
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>
            <Accordion
                disableGutters
                elevation={0}
                sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GroupAddIcon sx={{ color: 'primary.main' }} fontSize="small" />
                        <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                            Team Roster from{' '}
                        </Typography>
                        <img
                            src="/maxpreps-logo.png"
                            alt="MaxPreps"
                            style={{ height: 18, objectFit: 'contain' }}
                        />
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="https://www.maxpreps.com/.../baseball/roster/"
                            value={rosterUrl}
                            onChange={(e) => onRosterUrlChange(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') onImportRoster();
                            }}
                            disabled={importingRoster}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onImportRoster}
                            disabled={
                                importingRoster ||
                                !rosterUrl.trim() ||
                                !isValidMaxprepsUrl(rosterUrl)
                            }
                            startIcon={
                                importingRoster ? <CircularProgress size={16} /> : <GroupAddIcon />
                            }
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Import
                        </Button>
                    </Box>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default ImportRostersSection;
